// Fitting an uploaded drum loop to the beat grid (#430). Pure: the admin's
// browser decodes the file and hands its length here.
//
// The rule is build-drums.mjs's: a tempo is right when it divides the loop
// into whole beats. One allowance the build never needed: an upload may be an
// AAC or MP3 file whose decoded length carries the encoder's padding at the
// end, so the audio may run up to PADDING_SECONDS past the musical period.
// It may never be SHORTER than the period (by more than a fiftieth of a beat):
// the player would wrap past the end and play silence every cycle.

/** Most trailing audio an encoder adds, seconds (AAC pads ~50 ms at 44.1k). */
export const PADDING_SECONDS = 0.08;
/** How far short of a whole beat the audio may stop, in beats. */
const SHORT_BEATS = 0.02;

export interface LoopFit {
  bpm: number;
  beats: number;
  /** The musical period, beats × 60 / bpm — the player's loopEnd. */
  seconds: number;
  /** False when the tempo does not land the file on whole beats. */
  tempoVerified: boolean;
}

/** How a loop of `duration` seconds sits at `bpm`. */
export function fitLoop(duration: number, bpm: number): LoopFit | null {
  if (!(duration > 0) || !(bpm > 0)) return null;
  const beatSec = 60 / bpm;
  const raw = duration / beatSec;
  const beats = Math.round(raw);
  const onGrid =
    beats > 0 && raw - beats >= -SHORT_BEATS && (raw - beats) * beatSec <= PADDING_SECONDS;
  // Off the grid, never claim a period longer than the audio.
  const whole = onGrid ? beats : Math.max(1, Math.floor(raw + SHORT_BEATS));
  return {
    bpm,
    beats: whole,
    seconds: Number(((whole * 60) / bpm).toFixed(4)),
    tempoVerified: onGrid,
  };
}

/**
 * Whole-number tempos that land a loop of `duration` seconds on whole bars of
 * `beatsPerBar`, between 50 and 320 bpm, nearest to 120 first. The admin picks
 * one or types their own.
 */
export function suggestTempos(duration: number, beatsPerBar: number, max = 6): number[] {
  if (!(duration > 0) || !(beatsPerBar > 0)) return [];
  const found = new Set<number>();
  for (let bars = 1; bars <= 32; bars++) {
    const beats = bars * beatsPerBar;
    // The audio is the period plus at most the padding, so the tempo sits a
    // little above beats·60/duration; fitLoop decides each whole number near it.
    const exact = (beats * 60) / duration;
    const from = Math.max(50, Math.floor(exact * 0.98));
    const to = Math.min(320, Math.ceil(exact * 1.03));
    for (let bpm = from; bpm <= to; bpm++) {
      const fit = fitLoop(duration, bpm);
      if (fit?.tempoVerified && fit.beats === beats) found.add(bpm);
    }
  }
  return [...found].sort((a, b) => Math.abs(a - 120) - Math.abs(b - 120) || a - b).slice(0, max);
}

/** A key for an uploaded loop: never collides with a bundled one. */
export function uploadedLoopKey(id: string): string {
  return `upload-${id}`;
}

/** SHA-256 of the file, as the media catalogue writes it. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
